// Offscreen-canvas gradient header for the V2 PDF system.
//
// jsPDF has no gradient primitive, but PDF generation here is always
// client-side — so we paint the teal→blue rounded band on a 2× canvas and
// hand jsPDF a crisp PNG with true rounded corners.

import { PDF_V2, type RGB } from "./brand";

const rgb = (c: RGB) => `rgb(${c[0]}, ${c[1]}, ${c[2]})`;

/**
 * Returns a PNG data URL of the rounded teal→blue header band, or `null` if a
 * canvas isn't available (the builder then falls back to a flat fill).
 *
 * @param wMM band width in mm, @param hMM height in mm, @param rMM corner radius in mm
 */
export function gradientHeaderPNG(wMM: number, hMM: number, rMM: number): string | null {
  try {
    if (typeof document === "undefined") return null;
    const px = (mm: number) => Math.round(mm * (96 / 25.4) * 2); // 2× for sharpness
    const c = document.createElement("canvas");
    c.width = px(wMM);
    c.height = px(hMM);
    const ctx = c.getContext("2d");
    if (!ctx) return null;

    const r = px(rMM);
    ctx.beginPath();
    ctx.moveTo(r, 0);
    ctx.arcTo(c.width, 0, c.width, c.height, r);
    ctx.arcTo(c.width, c.height, 0, c.height, r);
    ctx.arcTo(0, c.height, 0, 0, r);
    ctx.arcTo(0, 0, c.width, 0, r);
    ctx.closePath();
    ctx.clip();

    const g = ctx.createLinearGradient(0, 0, c.width, 0);
    g.addColorStop(0, rgb(PDF_V2.color.grad.from));
    g.addColorStop(0.45, rgb(PDF_V2.color.grad.mid));
    g.addColorStop(1, rgb(PDF_V2.color.grad.to));
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, c.width, c.height);

    return c.toDataURL("image/png");
  } catch {
    return null;
  }
}
